import Breadcrumbs from "@/components/Breadcrumbs";
import { User, UserRole } from "@/services/admin-services/models";
import { UsersService } from "@/services/admin-services/UsersService";
import { ArchiveBoxArrowDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Collapse,
    Divider,
    Grid,
    IconButton,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const breadcrumbs = [
    {
        to: "../..",
        title: "Administration",
    },
    {
        title: "Users",
        to: "..",
    },
];

const Details = () => {
    const [breadcrumbTrail, setBreadcrumbTrail] = useState(breadcrumbs);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<string>();

    const [user, setUser] = useState<User>();
    const [roles, setRoles] = useState<UserRole[]>([]);
    const [userRoles, setUserRoles] = useState<string[]>([]);

    const [name, setName] = useState<string>("");
    const [email, setEmail] = useState<string>("");

    const [savedNotificationOpen, setSavedNotification] = useState<string>("");
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const theme = useTheme();
    const { identifier } = useParams();
    const navigate = useNavigate();

    const usersService = useMemo(() => new UsersService(), []);

    useEffect(() => {
        if (!identifier) return;

        Promise.all([
            usersService.getRoles().then(setRoles),
            usersService.getUser(identifier).then((user) => {
                setUser(user);

                if (user) {
                    usersService.getUserRoles(user.userId).then((assignedRoles) => {
                        setUserRoles(assignedRoles.map((x) => x.id));
                        setIsLoading(false);
                    });

                    setBreadcrumbTrail([...breadcrumbs, { title: user.fullName, to: "" }]);
                } else {
                    setBreadcrumbTrail([...breadcrumbs, { title: "User not found", to: "" }]);
                    setIsLoading(false);
                }
            }),
        ]);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!savedNotificationOpen) return;

        timerRef.current = setTimeout(() => setSavedNotification(""), 3000);
    }, [savedNotificationOpen]);

    useEffect(() => {
        if (!user) return;

        setName(user.fullName);
        setEmail(user.email);
    }, [user]);

    const handleRoleChange = (roleId: string) => {
        if (userRoles.includes(roleId)) {
            setUserRoles(userRoles.filter((x) => x !== roleId));
        } else {
            setUserRoles([...userRoles, roleId]);
        }
    };

    const onSaveInfo = () => {
        if (!user) return;

        setIsSaving("info");

        usersService.updateUser(user.userId, { name, email }).then((succes) => {
            setIsSaving("");

            if (!succes) return;

            setSavedNotification("Succesfully saved user information.");
            setUser({ ...user, fullName: name, email });
        });
    };

    const onCancelInfo = () => {
        if (!user) return;

        setName(user.fullName);
        setEmail(user.email);
    };

    const onSaveRoles = () => {
        if (!user) return;

        setIsSaving("roles");

        usersService
            .removeRoles(
                user.userId,
                roles.map((x) => x.id)
            )
            .then(() => {
                usersService.assignRoles(user.userId, userRoles).then((succes) => {
                    setIsSaving("");

                    if (!succes) return;

                    setSavedNotification("Succesfully saved user roles.");
                });
            });
    };

    const onCancelRoles = () => {
        if (!user) return;

        usersService.getUserRoles(user.userId).then((assignedRoles) => {
            setUserRoles(assignedRoles.map((x) => x.id));
            setIsLoading(false);
        });
    };

    const onBlockUser = () => {
        if (!user) return;

        setIsSaving("block");

        usersService.blockUser(user.userId).then((succes) => {
            setIsSaving("");

            if (!succes) return;

            setSavedNotification("Succesfully blocked user.");
            setUser({ ...user, blocked: true });
        });
    };

    const onUnblockUser = () => {
        if (!user) return;

        setIsSaving("block");

        usersService.unblockUser(user.userId).then((succes) => {
            setIsSaving("");

            if (!succes) return;

            setSavedNotification("Succesfully unblocked user.");
            setUser({ ...user, blocked: false });
        });
    };

    if (isLoading)
        return (
            <Box sx={{ display: "flex", height: 400, alignItems: "center", flexDirection: "column" }}>
                <Box sx={{ margin: "auto" }}>
                    <CircularProgress />
                </Box>
            </Box>
        );

    if (!user) {
        return (
            <Box>
                <Breadcrumbs trail={breadcrumbTrail} />

                <Box sx={{ display: "flex", alignItems: "center", m: 1 }}>
                    <Button variant="contained" color="primary" onClick={() => navigate("..")} sx={{ ml: "auto" }}>
                        Go back
                    </Button>
                </Box>

                <Box sx={{ display: "flex", minHeight: 400, alignItems: "center" }}>
                    <Typography variant="subtitle2" gutterBottom fontWeight={700} sx={{ m: "auto" }}>
                        User not found
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbTrail} />

            <Box sx={{ width: "100%" }}>
                <Collapse in={Boolean(savedNotificationOpen)}>
                    <Alert
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    setSavedNotification("");
                                }}
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </IconButton>
                        }
                        sx={{ mb: 2 }}
                    >
                        {savedNotificationOpen}
                    </Alert>
                </Collapse>
            </Box>

            <Grid
                container
                spacing={2}
                p={{ xs: theme.spacing(2), md: theme.spacing(4) }}
                sx={user.blocked ? { pointerEvents: "none", opacity: 0.5 } : {}}
            >
                <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="h6" gutterBottom fontWeight={700}>
                        User information
                    </Typography>
                    <Typography variant="subtitle2" color={"text.secondary"}>
                        Basic information
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                                Name
                            </Typography>
                            <TextField
                                label="Name"
                                variant="outlined"
                                fullWidth
                                value={name}
                                onChange={(e) => setName(e.currentTarget.value)}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant={"subtitle2"} sx={{ mb: 2 }} fontWeight={700}>
                                Email
                            </Typography>
                            <TextField
                                label="Email"
                                variant="outlined"
                                fullWidth
                                value={email}
                                onChange={(e) => setEmail(e.currentTarget.value)}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }} sx={{ display: "flex", alignContent: "flex-end" }}>
                            <Button
                                variant="contained"
                                color="error"
                                sx={{ ml: "auto" }}
                                onClick={() => onCancelInfo()}
                                disabled={Boolean(isSaving)}
                            >
                                Cancel
                            </Button>
                            <Box sx={{ ml: 1, position: "relative" }}>
                                <Button
                                    variant="contained"
                                    startIcon={<ArchiveBoxArrowDownIcon className="w-5 h-5" />}
                                    sx={{ ml: 1 }}
                                    onClick={() => onSaveInfo()}
                                    disabled={Boolean(isSaving)}
                                >
                                    Save
                                </Button>
                                {isSaving === "info" && (
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            marginTop: "-12px",
                                            marginLeft: "-12px",
                                        }}
                                    />
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Divider />

            <Grid
                container
                spacing={2}
                p={{ xs: theme.spacing(2), md: theme.spacing(4) }}
                sx={user.blocked ? { pointerEvents: "none", opacity: 0.5 } : {}}
            >
                <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="h6" gutterBottom fontWeight={700}>
                        Application Access
                    </Typography>
                    <Typography variant="subtitle2" color={"text.secondary"}>
                        Assigned roles
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                    <Grid container spacing={4}>
                        {roles.map((role) => (
                            <Grid size={{ xs: 12, sm: 6 }} key={role.id}>
                                <Box sx={{ display: "flex" }}>
                                    <Box>
                                        <Checkbox
                                            checked={userRoles.includes(role.id)}
                                            onChange={() => handleRoleChange(role.id)}
                                            disableRipple
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, pt: "10px" }}>
                                        <Typography variant={"subtitle2"} fontWeight={700}>
                                            {role.name}
                                        </Typography>
                                        <Typography variant={"caption"}>{role.description}</Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}

                        <Grid size={{ xs: 12 }} sx={{ display: "flex", alignContent: "flex-end" }}>
                            <Button
                                variant="contained"
                                color="error"
                                sx={{ ml: "auto" }}
                                onClick={() => onCancelRoles()}
                                disabled={Boolean(isSaving)}
                            >
                                Cancel
                            </Button>
                            <Box sx={{ ml: 1, position: "relative" }}>
                                <Button
                                    variant="contained"
                                    startIcon={<ArchiveBoxArrowDownIcon className="w-5 h-5" />}
                                    sx={{ ml: 1 }}
                                    onClick={() => onSaveRoles()}
                                    disabled={Boolean(isSaving)}
                                >
                                    Save
                                </Button>
                                {isSaving === "roles" && (
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            marginTop: "-12px",
                                            marginLeft: "-12px",
                                        }}
                                    />
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Divider />

            {!user.blocked ? (
                <Grid container spacing={2} p={{ xs: theme.spacing(2), md: theme.spacing(4) }}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="h6" gutterBottom fontWeight={700}>
                            Block user
                        </Typography>
                        <Typography variant="subtitle2" color={"text.secondary"}>
                            Disable the user and revoke access to the administration panel.
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, md: 8 }} sx={{ alignSelf: "center" }}>
                        <Grid container spacing={4}>
                            <Grid size={{ xs: 12, sm: 12 }}>
                                <Box sx={{ ml: 1, position: "relative" }}>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        fullWidth
                                        disabled={Boolean(isSaving)}
                                        onClick={onBlockUser}
                                    >
                                        Block user
                                    </Button>

                                    {isSaving === "block" && (
                                        <CircularProgress
                                            size={24}
                                            sx={{
                                                position: "absolute",
                                                top: "50%",
                                                left: "50%",
                                                marginTop: "-12px",
                                                marginLeft: "-12px",
                                            }}
                                        />
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            ) : (
                <Grid container spacing={2} p={{ xs: theme.spacing(2), md: theme.spacing(4) }}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="h6" gutterBottom fontWeight={700}>
                            Unblock user
                        </Typography>
                        <Typography variant="subtitle2" color={"text.secondary"}>
                            Enable the user again and restore access to the administration panel.
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, md: 8 }} sx={{ alignSelf: "center" }}>
                        <Grid container spacing={4}>
                            <Grid size={{ xs: 12, sm: 12 }}>
                                <Box sx={{ ml: 1, position: "relative" }}>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        fullWidth
                                        disabled={Boolean(isSaving)}
                                        onClick={onUnblockUser}
                                    >
                                        Unblock user
                                    </Button>

                                    {isSaving === "block" && (
                                        <CircularProgress
                                            size={24}
                                            sx={{
                                                position: "absolute",
                                                top: "50%",
                                                left: "50%",
                                                marginTop: "-12px",
                                                marginLeft: "-12px",
                                            }}
                                        />
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default Details;
